<template>
  <div v-if="item" class="order-item">
    <figure class="order-item__figure">
      <MenuImage :menu-item="item" />
    </figure>
    <div class="order-item__info">
      <p class="order-item__name">{{ getNameByLocale(item) }}</p>
      <div v-if="optionNames" class="order-item__description">ㄴ {{ optionNames }}</div>
    </div>
    <div>
      <span v-if="orderItem.RegDate">
        {{ dayjs(orderItem.RegDate).format('HH:mm:ss') }}
      </span>
    </div>
    <div class="order-item__qty">x{{ orderItem.QTY }}</div>
    <div class="order-item__price">{{ formatCurrency(amount) }}</div>
  </div>
</template>

<script setup lang="ts">
  import MenuImage from '@/components/MenuImage.vue';
  import OrderItem from '@/interfaces/OrderItem';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { formatCurrency, getMenuTaxAmount, getNameByLocale } from '@/utils';
  import dayjs from 'dayjs';
  import { computed } from 'vue';

  interface Props {
    orderItem: OrderItem;
  }

  const configStore = useConfigStore();
  const menuStore = useMenuStore();
  const props = defineProps<Props>();

  const item = menuStore.getItem(props.orderItem.ITEM_CODE);

  if (!item) throw new Error('아이템을 찾을 수 없습니다.');

  // 메뉴 총 금액
  const amount = computed(() => {
    /**
     * 경감세금 사용여부 체크
     * 일본 문화권 && 홀서빙 경감세금 사용 && 메뉴 경감세금 사용 3가지 모두 충족 시 경감세금 적용
     */
    let menuAmount = props.orderItem.Amt - (props.orderItem.DiscountAmt || 0);
    if (configStore.config.CultureName === 'ja-JP' && configStore.config.IsReduceTaxUsed && item.IsTax2) {
      // 경감 세금 설정이 '별도'이고 '세금 포함' 옵션을 사용하는 경우 메뉴 가격에 세금 합산
      if (configStore.config.TaxType2 === '1' && configStore.config.IsTaxIncluded) {
        menuAmount += getMenuTaxAmount(menuAmount, item.IsTax, item.IsTax2);
      }
    } else {
      // 일반 세금 설정이 '별도'이고 '세금 포함' 옵션을 사용하는 경우 메뉴 가격에 세금 합산
      if (configStore.config.TaxType1 === '1' && configStore.config.IsTaxIncluded) {
        menuAmount += getMenuTaxAmount(menuAmount, item.IsTax, item.IsTax2);
      }
    }

    return menuAmount;
  });

  const optionNames = computed(() => {
    if (props.orderItem.ItemType === 'M') {
      return props.orderItem.Modifier?.map((modifier) => {
        const findTopping = menuStore.toppings.find(
          (topping) =>
            topping.GroupCode === modifier.Modifier_Grp_Code && topping.ToppingCode === modifier.Modifier_Entity_Code,
        );
        return modifier.QTY > 1 ? `${getNameByLocale(findTopping)} x${modifier.QTY}` : getNameByLocale(findTopping);
      }).join(' / ');
    }

    if (props.orderItem.ItemType === 'S') {
      return props.orderItem.SetMenu?.map((orderSetMenu) => {
        const findSetMenu = menuStore.setMenus.find(
          (setMenu) =>
            setMenu.GroupCode.toString() == orderSetMenu.SetItem_Level && setMenu.SetMenuCode == orderSetMenu.ITEM_CODE,
        );

        return orderSetMenu.QTY > 1
          ? `${getNameByLocale(findSetMenu)} x${orderSetMenu.QTY}`
          : getNameByLocale(findSetMenu);
      }).join(' / ');
    }

    return '';
  });
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .order-item {
    display: grid;
    grid-template-columns: 4.231vw 1fr auto 2.2388vw 9.7015vw;
    gap: 1.9531vw;
    align-items: center;
    font-size: 1.6418vw;

    @media (max-width: vars.$lg) {
      grid-template-columns: 70px 1fr auto 30px 90px;
      gap: 10px;
      font-size: 20px;
    }
    @media (max-width: vars.$md) {
      grid-template-columns: 50px 1fr auto 20px 70px;
      gap: 10px;
      font-size: 14px;
    }

    .order-item__figure {
      height: auto;
      img {
        width: 100%;
        height: 100%;
        object-fit: scale-down;
      }
    }

    .order-item__info {
      .order-item__name {
        font-size: 1.6418vw;
        font-weight: 700;
        @media (max-width: vars.$lg) {
          font-size: 18px;
        }
        @media (max-width: vars.$md) {
          font-size: 14px;
        }
      }
      .order-item__description {
        font-size: 1.2687vw;
        color: var(--text-secondary);
        margin-top: 0.8955vw;
        @media (max-width: vars.$lg) {
          font-size: 16px;
        }
        @media (max-width: vars.$md) {
          font-size: 12px;
          margin-top: 6px;
        }
      }
    }
    .order-item__date {
      text-align: center;
    }
    .order-item__qty {
      text-align: center;
    }

    .order-item__price {
      text-align: right;
    }
  }
</style>
